import {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
} from "react"
import {
  X,
  Play,
  Loader2,
  Wrench,
  ChevronDown,
  AlertCircle,
  CheckCircle2,
  Clock3,
  Settings,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router"
import { format } from "date-fns"
import Markdown from "react-markdown"
import remarkGfm from "remark-gfm"
import remarkMath from "remark-math"
import rehypeKatex from "rehype-katex"
import rehypeRaw from "rehype-raw"
import "katex/dist/katex.min.css"

import { Badge, useSidebar } from "@f2e/ui/components/base"
import {
  ReasoningStepRenderer,
  ReasoningStepStatus,
  getReasoningStepTypeLabels,
} from "@f2e/ui/components/Chat"
import { ToastAction } from "@f2e/ui/components/base-legacy"

import {
  AttachmentCollector,
  FileUploadCollector,
  FileUploadCollectorHandle,
  HistoryConversationLink,
  UploadingFile,
} from "@/components/Conversation"
import { CopilotHiddenField } from "@/components/Copilot/CopilotHiddenField"
import { clampCopilotDrawerWidth } from "@/components/Copilot/drawerWidth"
import {
  Workspace,
  CaseEvaluatorAssignment,
  EvaluationResult,
  AgentHistory,
  ReasoningStep,
  EvaluationCaseLabel,
} from "@/types"
import { useStore } from "@/store"
import {
  useUpdateEvaluationCaseApi,
  useCreateEvaluationCaseApi,
  makeUpdateEvaluationCaseRequest,
  makeCreateEvaluationCaseRequest,
  useReplaceCaseEvaluatorInfosApi,
  makeReplaceCaseEvaluatorInfosRequest,
  useSetRubricApi,
  makeSetRubricRequest,
  useGetEvaluationResultsByCasesApi,
  makeGetEvaluationResultsByCasesRequest,
} from "@/hooks/api/eval"
import {
  transToCaseEvaluatorInfosByCase,
  transToEvaluationResult,
} from "@/hooks/api/types"
import { useToast, useWindowSize } from "@/hooks"
import { getUserFacingApiErrorToastProps } from "@/lib/apiErrors"
import {
  summarizeAttachments,
  summarizePreviousConversations,
} from "@/components/Copilot/summarizers"
import {
  formatCredits,
  formatUsageTime,
  summarizeUsageMeta,
} from "@/lib/usageMeta"
import { getLabelChipStyle } from "./labelColors"

import { GeneratingSpinner } from "./StatusIndicator"
import { Evaluator, EvaluationCase } from "./types"

const formatToolStepType = (type: string) => {
  return type.replace(/^consultant_/, "").replaceAll("_", " ")
}

const formatJsonBlock = (value: unknown) => {
  if (value === undefined || value === null) return ""
  if (typeof value === "string") return value
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

const sameStringSet = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false
  const aSet = new Set(a)
  return b.every((value) => aSet.has(value))
}

const sameStringMap = (
  a: Record<string, string>,
  b: Record<string, string>
) => {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  return aKeys.every((key) => a[key] === b[key])
}

type EvaluatorMetadataSaveAction = "set-rubric" | "replace" | null

export const getEvaluatorMetadataSaveAction = ({
  isEvaluationMode,
  hasSelectedEvaluator,
  selectedEvaluatorRubricChanged,
  assignedEvaluatorsChanged,
  evaluatorRubricsChanged,
}: {
  isEvaluationMode: boolean
  hasSelectedEvaluator: boolean
  selectedEvaluatorRubricChanged: boolean
  assignedEvaluatorsChanged: boolean
  evaluatorRubricsChanged: boolean
}): EvaluatorMetadataSaveAction => {
  if (isEvaluationMode) {
    return hasSelectedEvaluator && selectedEvaluatorRubricChanged
      ? "set-rubric"
      : null
  }
  return assignedEvaluatorsChanged || evaluatorRubricsChanged ? "replace" : null
}

const getAssignedRubricsByEvaluatorId = (
  evaluatorIds: string[],
  rubricsByEvaluatorId: Record<string, string>
) =>
  Object.fromEntries(
    evaluatorIds.map((evaluatorId) => [
      evaluatorId,
      rubricsByEvaluatorId[evaluatorId] || "",
    ])
  )

const getVisibleToolSteps = (steps: ReasoningStep[] | undefined) => {
  return steps?.filter((step) => step.type !== "consultant_finished")
}

const getToolStepKey = (step: ReasoningStep, index: number) => {
  return step.id || `${step.type}-${index}`
}

const getToolStepDurationMs = (step: ReasoningStep) => {
  if (typeof step.duration === "number" && Number.isFinite(step.duration)) {
    return step.duration
  }
  if (!step.startTime || !step.endTime) return undefined
  const duration =
    new Date(step.endTime).getTime() - new Date(step.startTime).getTime()
  return Number.isFinite(duration) && duration >= 0 ? duration : undefined
}

const formatToolStepDuration = (durationMs: number) => {
  if (durationMs < 1000) return `${Math.max(Math.round(durationMs), 1)}ms`
  const seconds = durationMs / 1000
  return seconds < 10 ? `${seconds.toFixed(1)}s` : `${Math.round(seconds)}s`
}

const getStringValue = (value: unknown) => {
  if (typeof value === "string" && value.trim()) return value.trim()
  return undefined
}

const getStringListPreview = (value: unknown) => {
  if (!Array.isArray(value)) return undefined
  const items = value
    .filter(
      (item): item is string => typeof item === "string" && item.trim() !== ""
    )
    .slice(0, 4)
  if (items.length === 0) return undefined
  const suffix =
    value.length > items.length ? ` +${value.length - items.length}` : ""
  return `${items.join(", ")}${suffix}`
}

const getToolStepPreview = (step: ReasoningStep) => {
  if (step.error) return step.error
  if (step.content?.trim()) return step.content.trim()

  const args = step.args
  if (!args) return undefined

  const stringPreview =
    getStringValue(args.question) ??
    getStringValue(args.query) ??
    getStringValue(args.prompt) ??
    getStringValue(args.url) ??
    getStringValue(args.pattern) ??
    getStringValue(args.agent_name) ??
    getStringValue(args.content)

  if (stringPreview) return stringPreview

  const listPreview =
    getStringListPreview(args.keywords) ??
    getStringListPreview(args.urls) ??
    getStringListPreview(args.filter_to_node_ids)

  return listPreview
}

const getToolStepStatus = (step: ReasoningStep) => {
  if (step.status === ReasoningStepStatus.Failed || step.error) return "failed"
  if (step.status === ReasoningStepStatus.Running) return "running"
  if (
    step.status === ReasoningStepStatus.Completed ||
    step.endTime ||
    step.result
  ) {
    return "completed"
  }
  return undefined
}

const hasToolStepDetails = (step: ReasoningStep) => {
  return Boolean(
    (step.args && Object.keys(step.args).length > 0) ||
    step.result ||
    step.error ||
    step.status === ReasoningStepStatus.Running
  )
}

export interface CaseDetailModalProps {
  isOpen: boolean
  onClose: () => void
  onCancel?: () => void
  caseData: EvaluationCase | null
  evaluators: Evaluator[]
  labels?: EvaluationCaseLabel[]
  initialEvaluator: Evaluator | undefined
  workspaceId: string | undefined
  evaluationResult: Partial<EvaluationResult> | undefined
  onFieldChange?: (
    caseId: string,
    updated: Partial<EvaluationCase> & {
      assignedEvaluators?: CaseEvaluatorAssignment[]
    },
    evaluatorId?: string
  ) => void
  onRunSingleCase?: (
    caseId: string,
    evaluatorId: string,
    agentHistoryId: string
  ) => void
  selectedAgentHistory: AgentHistory | null
  isCreateMode?: boolean
  variant?: "modal" | "panel"
  testerMode?: "independent" | "assignment" | "evaluation"
  onDirtyChange?: (dirty: boolean) => void
  onSavingChange?: (saving: boolean) => void
  onManageLabels?: () => void
}

const CaseDetailModal = ({
  isOpen,
  onClose,
  onCancel,
  caseData,
  evaluators,
  labels = [],
  initialEvaluator,
  workspaceId,
  evaluationResult,
  onFieldChange,
  onRunSingleCase,
  selectedAgentHistory,
  isCreateMode = false,
  variant = "modal",
  testerMode = "independent",
  onDirtyChange,
  onSavingChange,
  onManageLabels,
}: CaseDetailModalProps) => {
  const { t } = useTranslation()
  const { t: uiT } = useTranslation("ui")
  const { alertToast, successToast } = useToast()
  const location = useLocation()
  const { width: viewportWidth } = useWindowSize()
  const [copilotMode, isCopilotOpen, copilotDrawerWidth] = useStore((state) => [
    state.copilotMode,
    state.isCopilotOpen,
    state.copilotDrawerWidth,
  ])

  const drawerOffset =
    copilotMode === "drawer" && isCopilotOpen
      ? clampCopilotDrawerWidth({
          desiredWidth: copilotDrawerWidth,
          pathname: location.pathname,
          viewportWidth,
        })
      : 0

  const navigate = useNavigate()

  // File upload ref for drag-and-drop
  const fileUploadRef = useRef<FileUploadCollectorHandle>(null)

  // Custom success toast with navigation action
  const showSuccessToastWithAction = useCallback(
    (createdCaseId: string) => {
      if (!caseData?.agentId || !workspaceId) return

      const params = new URLSearchParams({
        tab: "evaluation",
        case_id: createdCaseId,
        clear_eval_filters: "1",
      })
      const evaluationUrl = `/workspaces/${workspaceId}/agents/${caseData.agentId}?${params.toString()}`

      successToast({
        title: t("evaluation.evaluationTable.message.createSuccess"),
        description: t("evaluation.evaluationTable.message.createSuccessDesc"),
        action: (
          <ToastAction
            altText={t("evaluation.evaluationTable.message.viewEvaluation")}
            onClick={() => navigate(evaluationUrl)}
          >
            {t("evaluation.evaluationTable.message.viewEvaluation")}
          </ToastAction>
        ),
      })
    },
    [caseData?.agentId, workspaceId, successToast, t, navigate]
  )

  const [selectedEvaluator, setSelectedEvaluator] = useState<
    Evaluator | undefined
  >(initialEvaluator)

  // Modal draft states for change tracking
  const [input, setInput] = useState(caseData?.input || "")
  const [expectedOutput, setExpectedOutput] = useState(
    caseData?.expectedOutput || ""
  )
  const [note, setNote] = useState(caseData?.note || "")
  const [attachments, setAttachments] = useState<UploadingFile[]>([])
  const [rubric, setRubric] = useState<string>("")
  const [rubricsByEvaluatorId, setRubricsByEvaluatorId] = useState<
    Record<string, string>
  >({})
  const [assignedEvaluatorIds, setAssignedEvaluatorIds] = useState<string[]>([])
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([])
  const [score, setScore] = useState<number | undefined>(undefined)
  const [reason, setReason] = useState<string>("")
  const [reasonUpdatedAt, setReasonUpdatedAt] = useState<string | undefined>(
    undefined
  )
  const [output, setOutput] = useState<string>("")
  const [outputUpdatedAt, setOutputUpdatedAt] = useState<string | undefined>(
    undefined
  )
  const [responseTimeMs, setResponseTimeMs] = useState<number | undefined>(
    evaluationResult?.responseTimeMs
  )
  const [costCredits, setCostCredits] = useState<number | undefined>(
    evaluationResult?.costCredits
  )
  const [judgeLlmModelId, setJudgeLlmModelId] = useState<string | undefined>(
    evaluationResult?.judgeLlmModelId
  )
  const [judgeLlmModelDisplayName, setJudgeLlmModelDisplayName] = useState<
    string | undefined
  >(evaluationResult?.judgeLlmModelDisplayName)
  const [reasoningSteps, setReasoningSteps] = useState<
    ReasoningStep[] | undefined
  >(evaluationResult?.reasoningSteps)

  // Saved states
  const [isSaving, setIsSaving] = useState(false)
  const [originalInput, setOriginalInput] = useState(caseData?.input || "")
  const [originalExpectedOutput, setOriginalExpectedOutput] = useState(
    caseData?.expectedOutput || ""
  )
  const [originalNote, setOriginalNote] = useState(caseData?.note || "")
  const [originalRubric, setOriginalRubric] = useState<string>("")
  const [originalRubricsByEvaluatorId, setOriginalRubricsByEvaluatorId] =
    useState<Record<string, string>>({})
  const [originalAttachments, setOriginalAttachments] = useState<
    UploadingFile[]
  >([])
  const [originalAssignedEvaluatorIds, setOriginalAssignedEvaluatorIds] =
    useState<string[]>([])
  const [originalSelectedLabelIds, setOriginalSelectedLabelIds] = useState<
    string[]
  >([])

  const [status, setStatus] = useState<string | undefined>(undefined)

  // API hooks for manual save
  const { fetchFn: updateCase } = useUpdateEvaluationCaseApi(caseData?.id || "")
  const { fetchFn: createCase } = useCreateEvaluationCaseApi()
  const { fetchFn: replaceCaseEvaluatorInfos } =
    useReplaceCaseEvaluatorInfosApi(caseData?.id || "")
  const { fetchFn: saveRubric } = useSetRubricApi()

  // API hooks batch results
  const { fetchFn: fetchResults } = useGetEvaluationResultsByCasesApi()

  const isModalVariant = variant === "modal"
  const isAssignmentMode = testerMode === "assignment"
  const isEvaluationMode = testerMode === "evaluation"
  const initializedDraftKeyRef = useRef<string | null>(null)
  const detailResultRequestIdRef = useRef(0)

  // Modal initialization effects
  useLayoutEffect(() => {
    if (!isModalVariant) return
    if (isOpen) {
      document.body.classList.add("overflow-hidden")
    } else {
      document.body.classList.remove("overflow-hidden")
    }
    return () => {
      document.body.classList.remove("overflow-hidden")
    }
  }, [isModalVariant, isOpen])

  // Set selectedEvaluator when modal opens or initialEvaluator changes
  useEffect(() => {
    if (!isOpen) return
    if (isAssignmentMode) {
      setSelectedEvaluator(undefined)
      return
    }
    if (isEvaluationMode) {
      setSelectedEvaluator(
        initialEvaluator &&
          (caseData?.assignedEvaluators || []).some(
            (assignment) => assignment.evaluatorId === initialEvaluator.id
          )
          ? initialEvaluator
          : undefined
      )
      return
    }
    if (
      initialEvaluator &&
      (isCreateMode ||
        (caseData?.assignedEvaluators || []).some(
          (assignment) => assignment.evaluatorId === initialEvaluator.id
        ))
    ) {
      setSelectedEvaluator(initialEvaluator)
      return
    }
    const firstAssignedId = caseData?.assignedEvaluators?.[0]?.evaluatorId
    setSelectedEvaluator(
      firstAssignedId
        ? evaluators.find((evaluator) => evaluator.id === firstAssignedId)
        : undefined
    )
  }, [
    isOpen,
    isAssignmentMode,
    isEvaluationMode,
    isCreateMode,
    initialEvaluator,
    caseData?.assignedEvaluators,
    evaluators,
  ])

  // Initialize editable drafts when the modal opens or the case identity changes.
  useEffect(() => {
    if (!isOpen || !caseData) {
      initializedDraftKeyRef.current = null
      return
    }

    const draftKey = `${isCreateMode ? "create" : "edit"}:${caseData.id}:${
      isCreateMode ? initialEvaluator?.id || "" : ""
    }`
    if (initializedDraftKeyRef.current === draftKey) return
    initializedDraftKeyRef.current = draftKey

    const inputValue = caseData.input || ""
    const expectedOutputValue = caseData.expectedOutput || ""
    const noteValue = caseData.note || ""
    const rubricValue = caseData.rubric || ""
    const attachmentsValue = caseData.attachments || []
    const labelIdsValue = (caseData.labels || []).map((label) => label.id)
    const assignedEvaluatorsValue =
      isCreateMode && initialEvaluator
        ? [{ evaluatorId: initialEvaluator.id, rubric: rubricValue }]
        : caseData.assignedEvaluators || []
    const assignedIdsValue = assignedEvaluatorsValue.map(
      (assignment) => assignment.evaluatorId
    )
    const rubricsByEvaluatorIdValue = Object.fromEntries(
      assignedEvaluatorsValue.map((assignment) => [
        assignment.evaluatorId,
        assignment.rubric || "",
      ])
    )

    setInput(inputValue)
    setExpectedOutput(expectedOutputValue)
    setNote(noteValue)
    setRubric(rubricValue)
    setRubricsByEvaluatorId(rubricsByEvaluatorIdValue)
    setAttachments(attachmentsValue)
    setAssignedEvaluatorIds(assignedIdsValue)
    setSelectedLabelIds(labelIdsValue)

    // Set original values for change tracking
    setOriginalInput(inputValue)
    setOriginalExpectedOutput(expectedOutputValue)
    setOriginalNote(noteValue)
    setOriginalRubric(rubricValue)
    setOriginalRubricsByEvaluatorId(rubricsByEvaluatorIdValue)
    setOriginalAttachments(attachmentsValue)
    setOriginalAssignedEvaluatorIds(assignedIdsValue)
    setOriginalSelectedLabelIds(labelIdsValue)
  }, [isOpen, caseData, isCreateMode, initialEvaluator])

  const assignedEvaluatorOptions = useMemo(
    () =>
      evaluators.filter((evaluator) =>
        assignedEvaluatorIds.includes(evaluator.id)
      ),
    [assignedEvaluatorIds, evaluators]
  )

  const handleLabelToggle = useCallback((labelId: string, checked: boolean) => {
    setSelectedLabelIds((current) =>
      checked
        ? [...current, labelId]
        : current.filter((currentLabelId) => currentLabelId !== labelId)
    )
  }, [])

  useEffect(() => {
    if (!isOpen || isCreateMode || testerMode !== "independent") return
    if (
      selectedEvaluator &&
      assignedEvaluatorIds.includes(selectedEvaluator.id)
    ) {
      return
    }
    setSelectedEvaluator(assignedEvaluatorOptions[0])
  }, [
    assignedEvaluatorIds,
    assignedEvaluatorOptions,
    isCreateMode,
    isOpen,
    selectedEvaluator,
    testerMode,
  ])

  useEffect(() => {
    if (!selectedEvaluator) {
      setRubric("")
      setOriginalRubric("")
      return
    }
    const rubricValue = rubricsByEvaluatorId[selectedEvaluator.id] || ""
    setRubric(rubricValue)
    setOriginalRubric(originalRubricsByEvaluatorId[selectedEvaluator.id] || "")
  }, [selectedEvaluator, rubricsByEvaluatorId, originalRubricsByEvaluatorId])

  const applyEvaluationResult = useCallback(
    (result?: Partial<EvaluationResult>) => {
      if (!result) {
        setScore(undefined)
        setReason("")
        setReasonUpdatedAt(undefined)
        setOutput("")
        setOutputUpdatedAt(undefined)
        setResponseTimeMs(undefined)
        setCostCredits(undefined)
        setJudgeLlmModelId(undefined)
        setJudgeLlmModelDisplayName(undefined)
        setReasoningSteps(undefined)
        setStatus(undefined)
        return
      }

      setJudgeLlmModelId(result.judgeLlmModelId)
      setJudgeLlmModelDisplayName(result.judgeLlmModelDisplayName)
      if (result.status !== "running" && result.status !== "pending") {
        setScore(result.score)
        setReason(result.reason || "")
        setReasonUpdatedAt(result.updatedAt)
        setOutput(result.output || "")
        setOutputUpdatedAt(result.outputUpdatedAt)
        setResponseTimeMs(result.responseTimeMs)
        setCostCredits(result.costCredits)
        setReasoningSteps(result.reasoningSteps)
      } else {
        setScore(undefined)
        setReason("")
        setReasonUpdatedAt(undefined)
        setOutput("")
        setOutputUpdatedAt(undefined)
        setResponseTimeMs(undefined)
        setCostCredits(undefined)
        setReasoningSteps(undefined)
      }
      setStatus(result.status)
    },
    []
  )

  // Fetch evaluation result when selectedEvaluator, caseData?.id, workspaceId, or selectedAgentHistory changes
  useEffect(() => {
    const requestId = ++detailResultRequestIdRef.current
    if (
      !isOpen ||
      !caseData?.id ||
      !selectedEvaluator?.id ||
      !workspaceId ||
      !selectedAgentHistory?.id ||
      isCreateMode
    ) {
      return
    }

    const target = {
      caseId: caseData.id,
      evaluatorId: selectedEvaluator.id,
      agentHistoryId: selectedAgentHistory.id,
    }
    applyEvaluationResult(undefined)
    const requestPayload = makeGetEvaluationResultsByCasesRequest(
      [target],
      workspaceId,
      true,
      true
    )

    void fetchResults({ jsonBody: requestPayload })
      .then((response) => {
        if (detailResultRequestIdRef.current !== requestId) return
        const result = response?.data
          ?.map(transToEvaluationResult)
          .find(
            (candidate) =>
              candidate.evaluationCaseId === target.caseId &&
              candidate.evaluatorId === target.evaluatorId &&
              candidate.agentHistoryId === target.agentHistoryId
          )
        applyEvaluationResult(result)
      })
      .catch((error) => {
        if (detailResultRequestIdRef.current !== requestId) return
        console.error("Failed to fetch evaluation results:", error)
        alertToast({
          ...getUserFacingApiErrorToastProps(error, t, {
            title: t("evaluation.evaluationTable.message.fetchResultsError"),
            fallbackDescription: t(
              "evaluation.evaluationTable.message.fetchResultsErrorDesc"
            ),
          }),
        })
      })
  }, [
    isOpen,
    isCreateMode,
    caseData?.id,
    selectedEvaluator?.id,
    workspaceId,
    selectedAgentHistory?.id,
    fetchResults,
    applyEvaluationResult,
    alertToast,
    t,
  ])

  // If evaluationResult matches selectedEvaluator, sync the completed result details.
  useEffect(() => {
    if (
      evaluationResult &&
      evaluationResult.evaluatorId &&
      evaluationResult.evaluatorId === selectedEvaluator?.id
    ) {
      applyEvaluationResult(evaluationResult)
    }
  }, [evaluationResult, selectedEvaluator, applyEvaluationResult])

  // Check if there are unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    // In create mode, always consider it as having changes (save button enabled)
    if (isCreateMode) {
      return true
    }

    const inputChanged = input !== originalInput
    const expectedOutputChanged = expectedOutput !== originalExpectedOutput
    const noteChanged = note !== originalNote
    const rubricChanged = rubric !== originalRubric
    const attachmentsChanged =
      JSON.stringify(attachments) !== JSON.stringify(originalAttachments)
    const assignedEvaluatorsChanged = !sameStringSet(
      assignedEvaluatorIds,
      originalAssignedEvaluatorIds
    )
    const labelsChanged = !sameStringSet(
      selectedLabelIds,
      originalSelectedLabelIds
    )
    const assignedRubricsByEvaluatorId = getAssignedRubricsByEvaluatorId(
      assignedEvaluatorIds,
      rubricsByEvaluatorId
    )
    const originalAssignedRubricsByEvaluatorId =
      getAssignedRubricsByEvaluatorId(
        originalAssignedEvaluatorIds,
        originalRubricsByEvaluatorId
      )
    const evaluatorRubricsChanged = !sameStringMap(
      assignedRubricsByEvaluatorId,
      originalAssignedRubricsByEvaluatorId
    )

    return (
      inputChanged ||
      expectedOutputChanged ||
      noteChanged ||
      rubricChanged ||
      attachmentsChanged ||
      assignedEvaluatorsChanged ||
      labelsChanged ||
      evaluatorRubricsChanged
    )
  }, [
    isCreateMode,
    input,
    originalInput,
    expectedOutput,
    originalExpectedOutput,
    note,
    originalNote,
    rubric,
    originalRubric,
    attachments,
    originalAttachments,
    assignedEvaluatorIds,
    originalAssignedEvaluatorIds,
    selectedLabelIds,
    originalSelectedLabelIds,
    rubricsByEvaluatorId,
    originalRubricsByEvaluatorId,
  ])

  const hasUnsavedDraft = hasUnsavedChanges()

  useEffect(() => {
    onDirtyChange?.(hasUnsavedDraft)
  }, [hasUnsavedDraft, onDirtyChange])

  useEffect(() => {
    onSavingChange?.(isSaving)
  }, [isSaving, onSavingChange])

  useEffect(
    () => () => {
      onSavingChange?.(false)
    },
    [onSavingChange]
  )

  // Create new evaluation case
  const handleCreate = useCallback(async () => {
    if (isSaving || !caseData?.agentId || !workspaceId) {
      return
    }

    setIsSaving(true)
    try {
      const attachmentIds = attachments
        .map((file) => file.uuid)
        .filter((uuid): uuid is string => !!uuid)
      const nextRubricsByEvaluatorId = {
        ...rubricsByEvaluatorId,
        ...(selectedEvaluator ? { [selectedEvaluator.id]: rubric } : {}),
      }
      const assignedEvaluators = assignedEvaluatorIds.map((evaluatorId) => ({
        evaluatorId,
        rubric: nextRubricsByEvaluatorId[evaluatorId] || "",
      }))

      // Create new case
      const createRequest = makeCreateEvaluationCaseRequest(
        caseData.agentId,
        input,
        expectedOutput,
        undefined,
        attachmentIds,
        caseData.meta,
        note,
        assignedEvaluators,
        selectedLabelIds
      )

      let createdCaseId: string | undefined

      // We use callbackFn to get the created case id because
      // the response is not immediately available
      await createCase({
        jsonBody: createRequest,
        callbackFn: (resp) => {
          if (!resp || !resp.data) {
            throw new Error("Failed to create evaluation case")
          }
          createdCaseId = resp.data.id
        },
      })

      if (!createdCaseId) {
        throw new Error("Failed to create evaluation case")
      }

      showSuccessToastWithAction(createdCaseId)

      // Close modal after successful creation
      onClose()
    } catch (error) {
      console.error("Failed to create case:", error)
      alertToast({
        ...getUserFacingApiErrorToastProps(error, t, {
          title: t("evaluation.evaluationTable.message.createError"),
          fallbackDescription: t(
            "evaluation.evaluationTable.message.createErrorDesc"
          ),
        }),
      })
    } finally {
      setIsSaving(false)
    }
  }, [
    selectedEvaluator,
    isSaving,
    caseData?.agentId,
    caseData?.meta,
    workspaceId,
    input,
    expectedOutput,
    note,
    attachments,
    rubric,
    rubricsByEvaluatorId,
    assignedEvaluatorIds,
    selectedLabelIds,
    createCase,
    onClose,
    alertToast,
    showSuccessToastWithAction,
    t,
  ])

  // Manual save function
  const saveChanges = useCallback(async () => {
    if (!caseData?.id || isSaving) {
      return
    }

    setIsSaving(true)
    try {
      // Save case fields
      const caseUpdates: Partial<EvaluationCase> = {}
      if (input !== originalInput) caseUpdates.input = input
      if (expectedOutput !== originalExpectedOutput)
        caseUpdates.expectedOutput = expectedOutput
      if (note !== originalNote) caseUpdates.note = note

      // Check if attachments changed
      const attachmentsChanged =
        JSON.stringify(attachments) !== JSON.stringify(originalAttachments)
      const labelsChanged = !sameStringSet(
        selectedLabelIds,
        originalSelectedLabelIds
      )
      const assignedEvaluatorsChanged = !sameStringSet(
        assignedEvaluatorIds,
        originalAssignedEvaluatorIds
      )
      const nextRubricsByEvaluatorId = {
        ...rubricsByEvaluatorId,
        ...(selectedEvaluator ? { [selectedEvaluator.id]: rubric } : {}),
      }
      const assignedEvaluators = assignedEvaluatorIds.map((evaluatorId) => ({
        evaluatorId,
        rubric: nextRubricsByEvaluatorId[evaluatorId] || "",
      }))
      const assignedRubricsByEvaluatorId = getAssignedRubricsByEvaluatorId(
        assignedEvaluatorIds,
        nextRubricsByEvaluatorId
      )
      const originalAssignedRubricsByEvaluatorId =
        getAssignedRubricsByEvaluatorId(
          originalAssignedEvaluatorIds,
          originalRubricsByEvaluatorId
        )
      const evaluatorRubricsChanged = !sameStringMap(
        assignedRubricsByEvaluatorId,
        originalAssignedRubricsByEvaluatorId
      )
      const evaluatorMetadataSaveAction = getEvaluatorMetadataSaveAction({
        isEvaluationMode,
        hasSelectedEvaluator: !!selectedEvaluator,
        selectedEvaluatorRubricChanged: rubric !== originalRubric,
        assignedEvaluatorsChanged,
        evaluatorRubricsChanged,
      })
      const attachmentIds = attachments
        .map((file) => file.uuid)
        .filter((uuid): uuid is string => !!uuid)

      // Update case if there are changes
      if (
        Object.keys(caseUpdates).length > 0 ||
        attachmentsChanged ||
        labelsChanged
      ) {
        const requestData = {
          ...caseUpdates,
          ...(attachmentsChanged ? { attachmentIds } : {}),
          ...(labelsChanged ? { labelIds: selectedLabelIds } : {}),
        }

        const requestPayload = makeUpdateEvaluationCaseRequest(requestData)
        await updateCase({
          jsonBody: requestPayload,
        })
      }

      let savedAssignedEvaluators: CaseEvaluatorAssignment[] =
        assignedEvaluators
      if (evaluatorMetadataSaveAction === "set-rubric" && selectedEvaluator) {
        await saveRubric({
          jsonBody: makeSetRubricRequest(
            caseData.id,
            selectedEvaluator.id,
            rubric
          ),
        })
        savedAssignedEvaluators = assignedEvaluators.map((assignment) =>
          assignment.evaluatorId === selectedEvaluator.id
            ? { ...assignment, rubric }
            : assignment
        )
      } else if (evaluatorMetadataSaveAction === "replace") {
        const response = await replaceCaseEvaluatorInfos({
          jsonBody: makeReplaceCaseEvaluatorInfosRequest(assignedEvaluators),
        })
        if (response?.data) {
          savedAssignedEvaluators = transToCaseEvaluatorInfosByCase(
            response.data
          ).evaluators
        }
      }
      const savedAssignedEvaluatorIds = savedAssignedEvaluators.map(
        (assignment) => assignment.evaluatorId
      )
      const savedRubricsByEvaluatorId = Object.fromEntries(
        savedAssignedEvaluators.map((assignment) => [
          assignment.evaluatorId,
          assignment.rubric || "",
        ])
      )

      // Update original values
      setOriginalInput(input)
      setOriginalExpectedOutput(expectedOutput)
      setOriginalNote(note)
      setOriginalRubric(rubric)
      setRubricsByEvaluatorId(savedRubricsByEvaluatorId)
      setOriginalRubricsByEvaluatorId(savedRubricsByEvaluatorId)
      setOriginalAttachments(attachments)
      setAssignedEvaluatorIds(savedAssignedEvaluatorIds)
      setOriginalAssignedEvaluatorIds(savedAssignedEvaluatorIds)
      setOriginalSelectedLabelIds(selectedLabelIds)

      // Notify parent component
      if (onFieldChange) {
        onFieldChange(
          caseData.id,
          {
            input,
            expectedOutput,
            note,
            attachments,
            labels: labels.filter((label) =>
              selectedLabelIds.includes(label.id)
            ),
            rubric,
            assignedEvaluators: savedAssignedEvaluators,
          },
          selectedEvaluator?.id
        )
      }

      successToast({
        title: t("evaluation.evaluationTable.message.saveSuccess"),
        description: t("evaluation.evaluationTable.message.saveSuccessDesc"),
      })
    } catch (error) {
      console.error("Failed to save changes:", error)
      alertToast({
        ...getUserFacingApiErrorToastProps(error, t, {
          title: t("evaluation.evaluationTable.message.saveError"),
          fallbackDescription: t(
            "evaluation.evaluationTable.message.saveErrorDesc"
          ),
        }),
      })
    } finally {
      setIsSaving(false)
    }
  }, [
    caseData?.id,
    selectedEvaluator,
    isSaving,
    input,
    originalInput,
    expectedOutput,
    originalExpectedOutput,
    note,
    originalNote,
    attachments,
    originalAttachments,
    selectedLabelIds,
    originalSelectedLabelIds,
    labels,
    assignedEvaluatorIds,
    originalAssignedEvaluatorIds,
    rubricsByEvaluatorId,
    originalRubricsByEvaluatorId,
    rubric,
    originalRubric,
    isEvaluationMode,
    updateCase,
    replaceCaseEvaluatorInfos,
    saveRubric,
    onFieldChange,
    alertToast,
    successToast,
    t,
  ])

  // Cancel changes function
  const cancelChanges = useCallback(() => {
    setInput(originalInput)
    setExpectedOutput(originalExpectedOutput)
    setNote(originalNote)
    setRubric(originalRubric)
    setRubricsByEvaluatorId(originalRubricsByEvaluatorId)
    setAttachments(originalAttachments)
    setAssignedEvaluatorIds(originalAssignedEvaluatorIds)
    setSelectedLabelIds(originalSelectedLabelIds)
    if (onCancel) {
      onCancel()
    } else {
      onClose()
    }
  }, [
    originalInput,
    originalExpectedOutput,
    originalNote,
    originalRubric,
    originalRubricsByEvaluatorId,
    originalAttachments,
    originalAssignedEvaluatorIds,
    originalSelectedLabelIds,
    onCancel,
    onClose,
  ])

  // Attachment unselect handler
  const handleAttachmentUnselect = useCallback(
    (item: UploadingFile) => {
      const newAttachments = attachments.filter(
        (attachment) => attachment.uuid !== item.uuid
      )
      setAttachments(newAttachments)
    },
    [attachments]
  )

  // Field change handlers
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => setInput(e.target.value),
    []
  )

  const handleExpectedOutputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) =>
      setExpectedOutput(e.target.value),
    []
  )

  const handleNoteChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value),
    []
  )

  const handleRubricChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const nextRubric = e.target.value
      setRubric(nextRubric)
      if (selectedEvaluator) {
        setRubricsByEvaluatorId((current) => ({
          ...current,
          [selectedEvaluator.id]: nextRubric,
        }))
      }
    },
    [selectedEvaluator]
  )
  const handleAttachmentsChange = useCallback(
    (files: UploadingFile[]) => setAttachments(files),
    []
  )

  // Handle run single case
  const handleRunSingleCase = () => {
    if (
      onRunSingleCase &&
      caseData &&
      selectedEvaluator &&
      selectedAgentHistory?.id
    ) {
      // Optimistically update local state to show running status immediately
      setScore(undefined)
      setReason("")
      setStatus("running")
      setReasoningSteps(undefined)

      // Call the parent handler
      onRunSingleCase(
        caseData.id,
        selectedEvaluator.id,
        selectedAgentHistory.id
      )
    }
  }

  const formattedResponseTime = useMemo(() => {
    return formatUsageTime(responseTimeMs) ?? undefined
  }, [responseTimeMs])
  const visibleToolSteps = useMemo(
    () => getVisibleToolSteps(reasoningSteps),
    [reasoningSteps]
  )
  const toolStepTypeLabels = useMemo(
    () => getReasoningStepTypeLabels(uiT),
    [uiT]
  )
  const [openToolStepIds, setOpenToolStepIds] = useState<string[]>([])

  useEffect(() => {
    setOpenToolStepIds((currentOpenIds) => {
      const stepKeys =
        visibleToolSteps?.map((step, index) => getToolStepKey(step, index)) ??
        []
      const validStepKeys = new Set(stepKeys)
      const preservedOpenIds = currentOpenIds.filter((id) =>
        validStepKeys.has(id)
      )
      if (preservedOpenIds.length > 0) return preservedOpenIds
      return stepKeys.length === 1 ? [stepKeys[0]] : []
    })
  }, [visibleToolSteps])

  // Get sidebar state to adjust modal positioning
  const { open: isSidebarOpen } = useSidebar()

  if (!isOpen || !caseData) return null

  const outerClassName = isModalVariant
    ? `fixed inset-0 bg-black/50 flex items-center justify-center z-50 ${
        isSidebarOpen ? "md:ml-(--sidebar-width)" : ""
      }`
    : "h-full min-h-0"
  const outerStyle = isModalVariant
    ? { right: drawerOffset, transition: "right 200ms ease-in-out" }
    : undefined
  const collectorClassName = isModalVariant
    ? "inline-block w-full md:w-4/5 max-w-7xl"
    : "block h-full"
  const shellClassName = isModalVariant
    ? "bg-white dark:bg-slate-900 rounded-lg shadow-xl max-h-[90vh] flex flex-col border border-gray-200 dark:border-slate-600"
    : "h-full min-h-0 bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 flex flex-col overflow-hidden"
  const headerClassName = isModalVariant
    ? "flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-600"
    : "flex items-start justify-between gap-3 p-4 border-b border-gray-200 dark:border-slate-700"
  const bodyClassName = isModalVariant
    ? "min-w-0 flex-1 overflow-y-auto py-6 px-14"
    : "min-w-0 flex-1 overflow-y-auto p-4"
  const contentLayoutClassName = isModalVariant
    ? "grid min-w-0 grid-cols-1 gap-6 md:grid-cols-2"
    : "flex min-w-0 flex-col gap-4"
  const footerClassName = isModalVariant
    ? "flex items-center justify-end p-6 border-t border-gray-200 dark:border-slate-800"
    : "flex items-center justify-end p-4 border-t border-gray-200 dark:border-slate-800"

  return (
    <div
      className={outerClassName}
      onClick={isModalVariant ? onClose : undefined}
      style={outerStyle}
    >
      <FileUploadCollector
        ref={fileUploadRef}
        workspace={workspaceId ? ({ id: workspaceId } as Workspace) : undefined}
        files={attachments}
        onStateChange={handleAttachmentsChange}
        isEvaluationContext={true}
        showDragOverlay={true}
        className={collectorClassName}
      >
        <div className={shellClassName} onClick={(e) => e.stopPropagation()}>
          <div className={headerClassName}>
            <h3 className="min-w-0 break-words text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t("evaluation.evaluationTable.detail.title")}{" "}
              {caseData.id ? `- ${caseData.id}` : ""}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className={bodyClassName}>
            {/* Hidden conceptual snapshot fields */}
            <CopilotHiddenField
              fieldKey="caseDetail.evaluator.options"
              value={evaluators.map((e) => e.name).join(" | ")}
            />
            <CopilotHiddenField
              fieldKey="caseDetail.usage"
              value={summarizeUsageMeta({
                responseTimeMs,
                costCredits,
              })}
            />
            <div className={contentLayoutClassName}>
              {/* Left: Text fields */}
              <div className="min-w-0 space-y-4">
                {/* History Conversations Section */}
                {caseData?.meta?.previousConversations &&
                  caseData?.meta?.previousConversations
                    ?.previousConversationCount !== 0 && (
                    <div
                      data-copilot-field="history.conversationsSummary"
                      data-copilot-value={summarizePreviousConversations(
                        caseData?.meta?.previousConversations
                      )}
                    >
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t(
                          "evaluation.evaluationTable.detail.historyConversations"
                        )}
                      </label>
                      <div className="border border-gray-200 dark:border-slate-700 rounded-lg p-3 bg-slate-50 dark:bg-slate-900">
                        <div className="flex min-w-0 flex-col justify-between gap-2 sm:flex-row sm:items-center">
                          <div className="flex min-w-0 items-center gap-3">
                            <Badge variant="outline" className="text-xs">
                              {
                                caseData.meta.previousConversations
                                  .previousConversationCount
                              }{" "}
                              {t(
                                "evaluation.evaluationTable.detail.conversationCount"
                              )}
                            </Badge>
                            <span className="min-w-0 text-sm text-slate-600 dark:text-slate-400">
                              {t(
                                "evaluation.evaluationTable.detail.historyDescription"
                              )}
                            </span>
                          </div>
                          <div className="shrink-0">
                            <HistoryConversationLink
                              workspaceId={workspaceId || ""}
                              historyId={
                                caseData.meta.previousConversations
                                  .sourceHistoryId
                              }
                              conversationId={
                                caseData.meta.previousConversations
                                  .targetConversationId
                              }
                            >
                              {t(
                                "evaluation.evaluationTable.detail.viewHistory"
                              )}
                            </HistoryConversationLink>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("evaluation.evaluationTable.detail.input")}
                  </label>
                  <textarea
                    data-copilot-field="caseDetail.input"
                    data-copilot-value={input}
                    className="w-full border border-gray-200 dark:border-slate-700 rounded p-2 min-h-[160px] max-h-[240px] resize-none bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100"
                    value={input}
                    onChange={handleInputChange}
                  />
                </div>
                <div
                  data-copilot-field="caseDetail.attachments"
                  data-copilot-value={summarizeAttachments(attachments)}
                >
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("evaluation.evaluationTable.detail.attachment")}
                  </label>
                  <AttachmentCollector
                    workspace={{ id: workspaceId || "" } as Workspace}
                    disabled={!isOpen}
                    initialFiles={attachments}
                    onAttachmentsChange={handleAttachmentsChange}
                    withIcon={true}
                    label={t("evaluation.evaluationTable.detail.attachment")}
                    onUnselect={handleAttachmentUnselect}
                    isEvaluationContext={true}
                    allowDownload={true}
                  />
                </div>
                {!isCreateMode &&
                  !isAssignmentMode &&
                  status !== "running" &&
                  status !== "pending" && (
                    <div
                      data-copilot-field="caseDetail.toolSteps"
                      data-copilot-value={formatJsonBlock(visibleToolSteps)}
                    >
                      <div className="mb-1 flex items-center justify-between">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t("evaluation.evaluationTable.detail.toolSteps")}
                        </label>
                        {visibleToolSteps && visibleToolSteps.length > 0 && (
                          <Badge variant="outline" className="text-[11px]">
                            {visibleToolSteps.length}
                          </Badge>
                        )}
                      </div>
                      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs dark:border-slate-700 dark:bg-slate-900">
                        {visibleToolSteps && visibleToolSteps.length > 0 ? (
                          <div className="divide-y divide-slate-200 dark:divide-slate-800">
                            {visibleToolSteps.map((step, index) => {
                              const stepKey = getToolStepKey(step, index)
                              const status = getToolStepStatus(step)
                              const durationMs = getToolStepDurationMs(step)
                              const preview = getToolStepPreview(step)
                              const canExpand = hasToolStepDetails(step)
                              const stepLabel =
                                toolStepTypeLabels[step.type] ||
                                formatToolStepType(step.type)

                              return (
                                <details
                                  key={stepKey}
                                  open={openToolStepIds.includes(stepKey)}
                                  onToggle={(event) => {
                                    const isOpen = event.currentTarget.open
                                    setOpenToolStepIds((currentOpenIds) => {
                                      if (isOpen) {
                                        return currentOpenIds.includes(stepKey)
                                          ? currentOpenIds
                                          : [...currentOpenIds, stepKey]
                                      }
                                      return currentOpenIds.filter(
                                        (id) => id !== stepKey
                                      )
                                    })
                                  }}
                                  className="group min-w-0 bg-white transition-colors open:bg-slate-50/70 dark:bg-slate-900 dark:open:bg-slate-950/40"
                                >
                                  <summary className="cursor-pointer list-none px-3 py-2.5 outline-none transition-colors hover:bg-slate-50 focus-visible:bg-slate-50 focus-visible:ring-2 focus-visible:ring-slate-900/10 dark:hover:bg-slate-800/60 dark:focus-visible:bg-slate-800/60 dark:focus-visible:ring-slate-50/20 [&::-webkit-details-marker]:hidden">
                                    <div className="flex min-w-0 items-start gap-3">
                                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-[11px] font-semibold text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                        {index + 1}
                                      </span>
                                      <Wrench className="mt-1 h-3.5 w-3.5 shrink-0 text-slate-400 dark:text-slate-500" />
                                      <div className="min-w-0 flex-1">
                                        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                                          <p className="min-w-0 truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                                            {stepLabel}
                                          </p>
                                          {status === "running" && (
                                            <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[11px] font-medium text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-300">
                                              <Loader2 className="h-3 w-3 animate-spin" />
                                              {uiT(
                                                "chat.reasoningSteps.status.running",
                                                "Running..."
                                              )}
                                            </span>
                                          )}
                                          {status === "failed" && (
                                            <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300">
                                              <AlertCircle className="h-3 w-3" />
                                              {uiT(
                                                "chat.reasoningSteps.status.failed",
                                                "Failed"
                                              )}
                                            </span>
                                          )}
                                          {status === "completed" && (
                                            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300">
                                              <CheckCircle2 className="h-3 w-3" />
                                              {uiT(
                                                "chat.reasoningSteps.status.completed",
                                                "Completed"
                                              )}
                                            </span>
                                          )}
                                          {durationMs !== undefined && (
                                            <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
                                              <Clock3 className="h-3 w-3" />
                                              {formatToolStepDuration(
                                                durationMs
                                              )}
                                            </span>
                                          )}
                                        </div>
                                        {preview && (
                                          <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
                                            {preview}
                                          </p>
                                        )}
                                      </div>
                                      {canExpand && (
                                        <ChevronDown className="mt-1 h-4 w-4 shrink-0 text-slate-400 transition-transform group-open:rotate-180" />
                                      )}
                                    </div>
                                  </summary>
                                  {canExpand && (
                                    <div className="border-t border-slate-200 bg-slate-50/80 px-3 py-3 dark:border-slate-800 dark:bg-slate-950/30">
                                      <ReasoningStepRenderer
                                        step={step}
                                        className="rounded-md border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900"
                                      />
                                    </div>
                                  )}
                                </details>
                              )
                            })}
                          </div>
                        ) : (
                          <p className="p-3 text-xs text-gray-500 dark:text-gray-400">
                            {t("evaluation.evaluationTable.detail.noToolSteps")}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                {!isCreateMode && !isAssignmentMode && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t("evaluation.evaluationTable.detail.output")}
                      </label>
                      {output && outputUpdatedAt && (
                        <div className="inline-flex items-center text-xs text-gray-500 dark:text-gray-400 px-2 py-1 bg-gray-100 dark:bg-slate-700 rounded-full">
                          {format(
                            new Date(outputUpdatedAt),
                            "MMM d, yyyy  HH:mm:ss"
                          )}
                        </div>
                      )}
                    </div>
                    <div
                      className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                      data-copilot-field="caseDetail.output"
                      data-copilot-value={output || ""}
                    >
                      <div className="p-3">
                        {status === "failed" && (!output || output === "") ? (
                          <div className="min-h-[160px] max-h-[240px] overflow-auto bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-md border border-red-200 dark:border-red-800 p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                              <span className="text-sm font-medium text-red-600 dark:text-red-400">
                                {t("evaluation.evaluationTable.status.failed")}
                              </span>
                            </div>
                            <p className="text-sm leading-relaxed break-words">
                              {t("evaluation.evaluationTable.status.noOutput")}
                            </p>
                          </div>
                        ) : status === "running" || status === "pending" ? (
                          <div className="flex items-center justify-center min-h-[160px]">
                            <GeneratingSpinner />
                          </div>
                        ) : (
                          <div className="min-h-[160px] max-h-[240px] overflow-auto">
                            <div className="prose min-w-0 break-words dark:prose-invert text-gray-700 dark:text-gray-200">
                              <Markdown
                                rehypePlugins={[rehypeRaw, rehypeKatex]}
                                remarkPlugins={[remarkGfm, remarkMath]}
                              >
                                {output}
                              </Markdown>
                            </div>
                          </div>
                        )}
                      </div>
                      {status === "running" || status === "pending" ? null : (
                        <div
                          className="border-t border-gray-200 dark:border-slate-700 p-3"
                          data-copilot-field="caseDetail.usage.panel"
                          data-copilot-value={summarizeUsageMeta({
                            responseTimeMs,
                            costCredits,
                          })}
                        >
                          {typeof costCredits === "number" ||
                          formattedResponseTime ? (
                            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
                              {typeof costCredits === "number" && (
                                <span className="font-semibold text-gray-900 dark:text-gray-100">
                                  {formatCredits(costCredits)} Credits
                                </span>
                              )}
                              {formattedResponseTime && (
                                <span>{formattedResponseTime} s</span>
                              )}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              {t("evaluation.evaluationTable.table.noUsage")}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("evaluation.evaluationTable.detail.expectedOutput")}
                  </label>
                  <textarea
                    className="w-full border border-gray-200 dark:border-slate-700 rounded p-2 min-h-[160px] max-h-[240px] resize-none bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100"
                    value={expectedOutput}
                    onChange={handleExpectedOutputChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("evaluation.evaluationTable.detail.note")}
                  </label>
                  <textarea
                    data-copilot-field="caseDetail.note"
                    data-copilot-value={note}
                    className="w-full border border-gray-200 dark:border-slate-700 rounded p-2 min-h-[120px] max-h-[240px] resize-none bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100"
                    value={note}
                    onChange={handleNoteChange}
                    placeholder={t(
                      "evaluation.evaluationTable.form.notePlaceholder"
                    )}
                  />
                </div>
              </div>
              {/* Right: Evaluator-specific fields */}
              <div className="flex min-w-0 w-full flex-col gap-4">
                <div>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t("evaluation.evaluationTable.detail.labels")}
                    </label>
                    {!isCreateMode && onManageLabels && (
                      <button
                        type="button"
                        className="flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                        onClick={onManageLabels}
                        title={t(
                          "evaluation.evaluationTable.action.manageLabels"
                        )}
                        aria-label={t(
                          "evaluation.evaluationTable.action.manageLabels"
                        )}
                      >
                        <Settings className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div
                    className="max-h-44 overflow-auto rounded border border-gray-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900"
                    data-copilot-field="caseDetail.labels"
                    data-copilot-value={labels
                      .filter((label) => selectedLabelIds.includes(label.id))
                      .map((label) => label.name)
                      .join(" | ")}
                  >
                    {labels.length === 0 ? (
                      <p className="px-1 py-2 text-sm text-gray-400 dark:text-gray-500">
                        {t("evaluation.evaluationTable.table.noLabels")}
                      </p>
                    ) : (
                      <div className="space-y-1">
                        {labels.map((label) => {
                          const checked = selectedLabelIds.includes(label.id)
                          return (
                            <label
                              key={label.id}
                              className="flex items-center gap-2 rounded px-2 py-1.5 text-sm text-gray-700 hover:bg-slate-50 dark:text-gray-300 dark:hover:bg-slate-800"
                            >
                              <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={checked}
                                onChange={(event) =>
                                  handleLabelToggle(
                                    label.id,
                                    event.target.checked
                                  )
                                }
                              />
                              <span
                                className="inline-flex min-w-0 max-w-full items-center rounded-full border px-2 py-0.5 text-xs"
                                style={getLabelChipStyle(label.color)}
                              >
                                <span className="truncate">{label.name}</span>
                              </span>
                            </label>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
                {!isEvaluationMode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t("evaluation.evaluationTable.detail.assignedTesters")}
                    </label>
                    <div
                      className="max-h-44 overflow-auto rounded border border-gray-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900"
                      data-copilot-field="caseDetail.assignedTesters"
                      data-copilot-value={assignedEvaluatorIds
                        .map(
                          (evaluatorId) =>
                            evaluators.find(
                              (evaluator) => evaluator.id === evaluatorId
                            )?.name || evaluatorId
                        )
                        .join(" | ")}
                    >
                      {evaluators.length === 0 ? (
                        <p className="px-1 py-2 text-sm text-gray-400 dark:text-gray-500">
                          {t("evaluation.evaluationTable.detail.noTesters")}
                        </p>
                      ) : (
                        <div className="space-y-1">
                          {evaluators.map((evaluator) => {
                            const isAssigned = assignedEvaluatorIds.includes(
                              evaluator.id
                            )
                            const isSelected =
                              selectedEvaluator?.id === evaluator.id
                            return (
                              <div
                                key={evaluator.id}
                                className={`flex items-start gap-2 rounded px-2 py-1.5 text-sm ${
                                  isSelected
                                    ? "bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-200"
                                    : "text-gray-700 hover:bg-slate-50 dark:text-gray-300 dark:hover:bg-slate-800"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  className="mt-0.5 h-4 w-4"
                                  checked={isAssigned}
                                  onChange={(event) => {
                                    setAssignedEvaluatorIds((current) =>
                                      event.target.checked
                                        ? [...current, evaluator.id]
                                        : current.filter(
                                            (id) => id !== evaluator.id
                                          )
                                    )
                                    if (event.target.checked) {
                                      if (!isAssignmentMode) {
                                        setSelectedEvaluator(evaluator)
                                      }
                                      setRubricsByEvaluatorId((current) => ({
                                        ...current,
                                        [evaluator.id]:
                                          current[evaluator.id] || "",
                                      }))
                                    } else if (!isAssignmentMode) {
                                      setRubricsByEvaluatorId((current) => {
                                        const next = { ...current }
                                        delete next[evaluator.id]
                                        return next
                                      })
                                      if (isSelected) {
                                        const nextAssignedEvaluator =
                                          assignedEvaluatorOptions.find(
                                            (option) =>
                                              option.id !== evaluator.id
                                          )
                                        setSelectedEvaluator(
                                          nextAssignedEvaluator
                                        )
                                      }
                                    }
                                  }}
                                />
                                <span className="min-w-0 flex-1">
                                  {isAssignmentMode ? (
                                    <span className="block truncate">
                                      {evaluator.name}
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      className="block w-full truncate text-left disabled:cursor-not-allowed disabled:opacity-50"
                                      disabled={!isAssigned}
                                      onClick={() =>
                                        setSelectedEvaluator(evaluator)
                                      }
                                      data-copilot-field={
                                        isSelected
                                          ? "caseDetail.evaluator.selected"
                                          : undefined
                                      }
                                      data-copilot-value={
                                        isSelected ? evaluator.name : undefined
                                      }
                                    >
                                      {evaluator.name}
                                    </button>
                                  )}
                                </span>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {!isAssignmentMode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t("evaluation.evaluationTable.detail.rubric")}
                    </label>
                    <textarea
                      data-copilot-field="caseDetail.rubric"
                      data-copilot-value={rubric}
                      className="w-full border border-gray-200 dark:border-slate-700 rounded p-2 min-h-[240px] max-h-[480px] resize-none bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      value={rubric}
                      onChange={handleRubricChange}
                      disabled={!selectedEvaluator}
                    />
                  </div>
                )}
                {!isCreateMode &&
                  !isAssignmentMode &&
                  (judgeLlmModelDisplayName || judgeLlmModelId) && (
                    <div
                      data-copilot-field="caseDetail.judgeLlmModel"
                      data-copilot-value={
                        judgeLlmModelDisplayName || judgeLlmModelId
                      }
                    >
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t("evaluation.evaluationTable.detail.evaluationModel")}
                      </label>
                      <p
                        className="text-sm text-gray-700 dark:text-gray-200"
                        title={judgeLlmModelId}
                      >
                        {judgeLlmModelDisplayName || judgeLlmModelId}
                      </p>
                    </div>
                  )}
                {!isCreateMode && !isAssignmentMode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t("evaluation.evaluationTable.detail.score")}
                    </label>
                    <div
                      className="flex items-center min-h-[40px]"
                      data-copilot-field="caseDetail.score"
                      data-copilot-value={
                        typeof score === "number" && Number.isFinite(score)
                          ? score.toFixed(2)
                          : ""
                      }
                    >
                      {status === "running" || status === "pending" ? (
                        <GeneratingSpinner />
                      ) : (
                        <span className="inline-block text-3xl font-bold bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200 rounded-full px-6 py-2">
                          {typeof score === "number"
                            ? score.toFixed(2)
                            : t("evaluation.evaluationTable.detail.noScore")}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {!isCreateMode && !isAssignmentMode && (
                  <div
                    data-copilot-field="caseDetail.reason"
                    data-copilot-value={reason || ""}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t("evaluation.evaluationTable.detail.reason")}
                      </label>
                      {reason && reasonUpdatedAt && (
                        <div className="inline-flex items-center text-xs text-gray-500 dark:text-gray-400 px-2 py-1 bg-gray-100 dark:bg-slate-700 rounded-full">
                          {format(
                            new Date(reasonUpdatedAt),
                            "MMM d, yyyy  HH:mm:ss"
                          )}
                        </div>
                      )}
                    </div>
                    {status === "failed" ? (
                      <div className="w-full rounded p-3 min-h-[120px] max-h-[240px] resize-none text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 overflow-auto">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                          <span className="text-sm font-medium text-red-600 dark:text-red-400">
                            {t("evaluation.evaluationTable.status.failed")}
                          </span>
                        </div>
                        <p className="text-sm leading-relaxed break-words">
                          {reason ||
                            t("evaluation.evaluationTable.detail.noReason")}
                        </p>
                      </div>
                    ) : status === "running" || status === "pending" ? (
                      <div className="flex items-center min-h-[40px]">
                        <GeneratingSpinner />
                      </div>
                    ) : (
                      <p className="w-full rounded p-2 min-h-[120px] max-h-[240px] resize-none text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-slate-900 overflow-auto whitespace-pre-wrap break-words">
                        {reason || ""}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={footerClassName}>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {isModalVariant && (
                <button
                  type="button"
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition disabled:opacity-50"
                  onClick={cancelChanges}
                  disabled={isSaving}
                >
                  {t("workspace.cancel")}
                </button>
              )}
              {isCreateMode ? (
                <button
                  type="button"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                  onClick={handleCreate}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                      {t("workspace.creating")}
                    </>
                  ) : (
                    t("workspace.create")
                  )}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                    onClick={saveChanges}
                    disabled={!hasUnsavedDraft || isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                        {t("workspace.saving")}
                      </>
                    ) : (
                      t("workspace.save")
                    )}
                  </button>
                  {!isAssignmentMode && (
                    <button
                      type="button"
                      className="flex items-center gap-1 px-3 py-2 bg-primary text-white dark:text-gray-900 rounded-lg hover:bg-primary/90 dark:hover:bg-primary/80 transition disabled:opacity-60"
                      onClick={handleRunSingleCase}
                      disabled={
                        !selectedEvaluator ||
                        status === "running" ||
                        hasUnsavedDraft
                      }
                    >
                      {status === "running" ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4 mr-1" />
                      )}
                      <span className="font-medium">
                        {status === "running"
                          ? t("evaluation.evaluationTable.action.running")
                          : t("evaluation.evaluationTable.action.run")}
                      </span>
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </FileUploadCollector>
    </div>
  )
}

export default CaseDetailModal
